import { colors } from "./colors";

/**
 * Couche SÉMANTIQUE — l'API couleur du NOUVEAU code (pattern Base / Uber).
 *
 * ## Deux couches, une règle
 * `colors.*` = PRIMITIVES (rampes brutes : or500, navy800, sand50…). On n'en
 * consomme quasiment jamais directement dans le code applicatif.
 * `semantic.*` = RÔLES (contentPrimary, backgroundScreen, borderCard…). C'est
 * ce qu'un écran importe. Le jour où l'on veut re-thémer (mode sombre, palette
 * saisonnière, ajustement de température), on remappe ICI — pas dans 130 fichiers.
 *
 * ## Pourquoi la propagation warm ne dépend pas (encore) de cette couche
 * La rampe neutre a été réchauffée à la source (cf. colors.ts) → toute l'app est
 * déjà chaude sans migration. Cette couche capitalise ce choix en RÔLES nommés :
 * le code neuf s'y branche, l'ancien migre progressivement. Aucune régression à
 * attendre — les valeurs pointées sont celles déjà utilisées.
 *
 * ## Séparation MARQUE vs SÉMANTIQUE (règle Base)
 * `*Brand` porte l'orange. `positive/warning/danger/info` sont des états système
 * et n'empruntent JAMAIS un token de marque (un warning n'est pas orange).
 */
export const semantic = {
  // ── Contenu (texte, icônes) ───────────────────────────────────────────────
  contentPrimary:   colors.navy800,   // titres, corps principal
  contentSecondary: colors.gray500,   // sous-titres, légendes
  contentTertiary:  colors.gray500,   // hints, placeholders
  contentDisabled:  colors.gray300,   // désactivé
  contentBrand:     colors.or700,     // texte/lien de marque (cran AA sur clair)
  contentOnColor:   colors.white,     // texte sur surface SOMBRE (navy, sos)
  // ⚠️ NAVY et non blanc : la marque est un OR clair (#d4a017). Du blanc
  // dessus ne ferait que 2.4:1 — illisible. Le navy donne 7.9:1.
  // C'est LE token à utiliser pour tout texte/icône posé sur un aplat or.
  // ⚠️ PAS `colors.navy800` : cette rampe s'INVERSE en thème sombre (le navy
  // est le texte principal, il devient clair). Or l'or de marque reste clair
  // dans les deux thèmes — un libellé clair sur un CTA or serait illisible.
  // Ce token garde donc sa propre variable, sombre en permanence.
  contentOnBrand:   "var(--c-onBrand)",

  // ── Fonds & surfaces ──────────────────────────────────────────────────────
  backgroundScreen:  colors.white,    // canevas d'écran — BLANC partout (PO)
  backgroundSurface: colors.white,    // cartes, feuilles
  backgroundSubtle:  colors.gray100,  // champs remplis, pistes, chips (gris froid)
  backgroundBrand:   colors.or500,    // CTA plein (texte blanc dessus)
  backgroundBrandSubtle: colors.or50, // pastille/halo de marque
  backgroundInverse: colors.navy800,  // surface SOMBRE sur UI claire (chip, FAB)
  backgroundOverlay: "rgba(11,18,32,0.5)", // scrim de modal/bottom-sheet

  // ── Bordures ──────────────────────────────────────────────────────────────
  borderCard:   colors.gray200,       // contour de carte
  borderSubtle: colors.gray100,       // séparateurs, dividers
  borderStrong: colors.navy800,       // focus, sélection

  // ── États système (JAMAIS la marque) ──────────────────────────────────────
  positive:        colors.success600, // texte de succès (AA sur clair)
  positiveSurface: colors.success100,
  warning:         colors.warn600,    // texte d'avertissement (AA sur clair)
  warningSurface:  colors.warn50,
  danger:          colors.sos600,     // texte/urgence
  dangerSurface:   colors.sos200,
  info:            colors.navy800,
  infoSurface:     colors.navy50,

  // ── Signaux spécifiques métier ────────────────────────────────────────────
  live:    colors.emerald600,  // course active / Main en route
  premium: colors.gold500,     // tier "Main d'Or"
} as const;

export type SemanticToken = keyof typeof semantic;
